import { Building2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { EDITABLE_TOKENS } from "@/lib/theme/colors";
import Segmented from "@/components/appearance/Segmented";
import PresetCard from "@/components/appearance/PresetCard";
import ColorField from "@/components/appearance/ColorField";
import ContrastIssues, { mergeFixes } from "@/components/appearance/ContrastIssues";

function Section({ title, hint, children }) {
  return (
    <section className="space-y-2.5">
      <div>
        <h3 className="text-sm font-semibold text-le-text">{title}</h3>
        {hint && <p className="mt-0.5 text-xs text-le-t-2">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function CustomColours({ settings, onChange, eff, presets, issues }) {
  const byId = Object.fromEntries(presets.map((p) => [p.id, p]));
  const base = byId[settings.preset_id]?.tokens || eff.clinic.tokens;
  const overrides = settings.overrides || {};
  const flagged = new Set(issues.flatMap((i) => i.tokens));
  const setColour = (k, v) => onChange({ overrides: { ...overrides, [k]: v } });
  return (
    <Section title="Custom colours" hint="Start from a theme, then change any colour. Unreadable combinations are flagged.">
      <Select value={settings.preset_id || "__clinic"} onValueChange={(v) => onChange({ preset_id: v === "__clinic" ? null : v })}>
        <SelectTrigger className="w-full" aria-label="Start from" data-testid="custom-start-from">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__clinic">Start from the clinic theme ({eff.clinic.preset_name})</SelectItem>
          {presets.filter((p) => p.allowed).map((p) => (
            <SelectItem key={p.id} value={p.id}>Start from {p.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="divide-y divide-zinc-100 rounded-lg border border-le-border bg-le-surface px-3">
        {EDITABLE_TOKENS.map((k) => (
          <ColorField key={k} token={k} value={overrides[k] || base[k]} onChange={(v) => setColour(k, v)} flagged={flagged.has(k)} />
        ))}
      </div>
      <ContrastIssues issues={issues} onFix={() => onChange({ overrides: { ...overrides, ...mergeFixes(issues) } })} />
      {Object.keys(overrides).length > 0 && (
        <Button variant="ghost" size="sm" onClick={() => onChange({ overrides: {} })} data-testid="custom-clear-btn">
          <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Undo my colour changes
        </Button>
      )}
    </Section>
  );
}

export default function ThemeEditor({ settings, onChange, eff, presets, issues, compact }) {
  const caps = eff.capabilities;
  const allowed = presets.filter((p) => p.allowed);
  const setMode = (mode) => onChange({
    mode,
    preset_id: mode === "preset" ? (allowed.some((p) => p.id === settings.preset_id) ? settings.preset_id : allowed[0]?.id) : mode === "custom" ? settings.preset_id : null,
  });
  return (
    <div className="space-y-6" data-testid="theme-editor">
      <Section title="Theme" hint="Only you see your choice. Other staff and the clinic default are not affected.">
        <Segmented
          label="Theme source"
          testId="theme-mode"
          value={settings.mode}
          onChange={setMode}
          options={[
            { value: "clinic", label: "Clinic default" },
            { value: "preset", label: "Preset", disabled: !allowed.length },
            { value: "custom", label: "Custom", disabled: !caps.allow_custom, hint: caps.allow_custom ? undefined : "Your clinic has turned off custom colours" },
          ]}
        />
        {!caps.allow_custom && <p className="text-xs text-le-t-2">Custom colours are turned off by the clinic owner.</p>}
        {!caps.allow_presets && <p className="text-xs text-le-t-2">The clinic owner allows only the High Contrast theme as a personal choice.</p>}
      </Section>

      {settings.mode === "clinic" && (
        <div className="flex items-start gap-3 rounded-lg border border-le-border bg-le-subtle p-3" data-testid="clinic-default-info">
          <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-le-primary-text" aria-hidden />
          <p className="text-sm text-le-t-body">
            You&apos;ll see the clinic theme, <span className="font-semibold text-le-text">{eff.clinic.preset_name}</span>. It updates automatically when the owner publishes a new clinic default.
          </p>
        </div>
      )}

      {settings.mode === "preset" && (
        <Section title="Choose a theme">
          <div className={cn("grid gap-3", compact ? "grid-cols-2" : "grid-cols-2 xl:grid-cols-4")}>
            {allowed.map((p) => (
              <PresetCard key={p.id} preset={p} selected={settings.preset_id === p.id} onSelect={(id) => onChange({ preset_id: id })} />
            ))}
          </div>
        </Section>
      )}

      {settings.mode === "custom" && caps.allow_custom && (
        <CustomColours settings={settings} onChange={onChange} eff={eff} presets={presets} issues={issues} />
      )}

      <Section title="Display" hint="Light mode only for now.">
        <div className="flex flex-wrap gap-x-6 gap-y-3">
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-le-t-2" id="density-label">Density</div>
            <Segmented label="Density" testId="theme-density" value={settings.density} onChange={(v) => onChange({ density: v })}
              options={[{ value: "comfortable", label: "Comfortable" }, { value: "compact", label: "Compact" }]} />
          </div>
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-le-t-2">Text size</div>
            <Segmented label="Text size" testId="theme-text-size" value={settings.text_scale} onChange={(v) => onChange({ text_scale: v })}
              options={[{ value: "standard", label: "Standard" }, { value: "large", label: "Large" }]} />
          </div>
        </div>
      </Section>
    </div>
  );
}
